/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import {
  ItemFocusDetail,
  ItemHoverDetail,
  ItemInfo,
  LegendItemData,
  LegendSectionData
} from '../utils/UNSAFE_visTypes/legend';

function getItem<K, D>(itemInfo: ItemInfo, sections: LegendSectionData<K, D>[]) {
  const { sectionIdx, itemIdx } = itemInfo;

  if (sectionIdx == -1 && itemIdx == -1) {
    const itemsLength = sections[sections.length - 1].items.length;
    return sections[sections.length - 1].items[itemsLength - 1];
  }

  return sections[sectionIdx].items[itemIdx];
}

function getNextItemInfo<K, D>(itemInfo: ItemInfo, sections: LegendSectionData<K, D>[]) {
  let { itemIdx, sectionIdx } = itemInfo;

  if (sectionIdx == -1 && itemIdx == -1) {
    return {
      itemIdx: sections[sections.length - 1].items.length - 1,
      sectionIdx: sections.length - 1
    };
  }

  itemIdx += 1;
  const itemsLength = sections[sectionIdx].items.length;
  if (itemIdx >= itemsLength) {
    if (sectionIdx === sections.length - 1) {
      itemIdx = itemInfo.itemIdx;
    } else {
      sectionIdx += 1;
      itemIdx = 0;
    }
  }
  const item = sections[sectionIdx].items[itemIdx] as LegendItemData<D>; //TODO : Work for better fix instead of adding 'as LegendItem<D>'
  if (item?.actionable !== 'off') {
    return { itemIdx, sectionIdx };
  }
  return getNextItemInfo({ itemIdx, sectionIdx }, sections);
}

function getPrevItemInfo<K, D>(itemInfo: ItemInfo, sections: LegendSectionData<K, D>[]) {
  let { itemIdx, sectionIdx } = itemInfo;

  if (sectionIdx == -1 && itemIdx == -1) {
    (itemIdx = sections[sections.length - 1].items.length - 1), (sectionIdx = sections.length - 1);
  }
  itemIdx -= 1;

  if (itemIdx < 0) {
    if (sectionIdx === 0) {
      itemIdx = 0;
    } else {
      itemIdx = sections[sectionIdx - 1].items.length - 1;
      sectionIdx -= 1;
    }
  }

  const item = sections[sectionIdx].items[itemIdx] as LegendItemData<D>; //TODO : Work for better fix instead of adding 'as LegendItem<D>'
  if (item?.actionable !== 'off') {
    return { itemIdx, sectionIdx };
  }
  return getPrevItemInfo({ itemIdx, sectionIdx }, sections);
}

/**
 * Returns utils functions for navigation in simple sectional legend.
 * @param items
 * @returns
 */
export function getSectionalNavUtils<
  K extends string | number,
  D extends LegendItemData<K>,
  S extends LegendSectionData<K, D>
>(sections: S[]) {
  return {
    getItem: (itemInfo: ItemInfo) => {
      return getItem(itemInfo, sections);
    },
    getDetailFromInfo: (itemInfo?: ItemInfo): ItemFocusDetail<K, D> | ItemHoverDetail<K, D> => {
      if (!itemInfo) {
        return { itemId: undefined, sectionId: undefined, data: undefined };
      }
      const item = getItem(itemInfo, sections);
      const sectionId = sections[itemInfo.sectionIdx].id;
      return {
        itemId: item.id,
        sectionId: sectionId,
        data: sections[itemInfo.sectionIdx].items[itemInfo.itemIdx]
      };
    },
    getPrevItemInfo: (itemInfo: ItemInfo) => {
      return getPrevItemInfo(itemInfo, sections);
    },
    getNextItemInfo: (itemInfo: ItemInfo) => {
      return getNextItemInfo(itemInfo, sections);
    }
  };
}
